// Child analyses (IMPL_ADDENDUM §2d): the backend queues each dropped PE or script of a run as its own job.
//   <ParentLink parentId />   "child of invoice_2026_scan.exe →" under the report title
//   <ChildJobs job />         small sheet of jobs whose parent_job_id is this run (from the jobs list):
//                             sample (link) · verdict banner, LIVE (+ overdue) or QUEUED · submitted (with the date when
//                             it is not the parent's UTC day). The list is polled only while children can still appear
//                             or run (useChildJobs).
import { Link } from "react-router-dom";
import { Arrow, JobState, LiveBadge, QueuedBadge, Time, VerdictBanner } from "@/components/ui";
import { useChildJobs, useClock, useJob } from "@/hooks";
import { hash8, utc } from "@/lib/format";
import { jobName, recordLiveness } from "@/lib/records";
import type { Job } from "@/types";

export function ParentLink({ parentId }: { parentId: string }) {
  const { data: parent } = useJob(parentId);
  return (
    <p className="mt-3">
      <Link className="action" to={`/jobs/${parentId}`} viewTransition>
        child of{" "}
        <span className="text-ink" translate="no">
          {parent ? jobName(parent) : hash8(parentId)}
        </span>
        <Arrow />
      </Link>
    </p>
  );
}

function StateCell({ job, now }: { job: Job; now: number }) {
  if (job.verdict) return <VerdictBanner v={job.verdict.label} score={job.verdict.score} size="sm" />;
  const l = recordLiveness(job, now);
  if (l === "queued") return <QueuedBadge size="sm" />;
  if (l === "live") return <LiveBadge size="sm" />;
  if (l === "overdue")
    return (
      <span className="inline-flex items-center gap-2">
        <LiveBadge size="sm" ping={false} />
        <span className="meta leading-none">overdue</span>
      </span>
    );
  return <JobState s={job.state} />;
}

const dayKey = (iso: string) => (Number.isFinite(Date.parse(iso)) ? new Date(iso).toISOString().slice(0, 10) : "");

export function ChildJobs({ job }: { job: Job }) {
  const children = useChildJobs(job);
  const now = useClock(15_000);
  if (!children.length) return null;
  return (
    <section className="mt-8" aria-labelledby="child-analyses">
      <p className="subhead flex-wrap" id="child-analyses">
        Child analyses <span className="ct">{children.length}</span>
      </p>
      <div className="tscroll">
        <table className="t t--sm">
          <thead>
            <tr>
              <th>sample</th>
              <th>verdict · state</th>
              <th className="r">submitted</th>
            </tr>
          </thead>
          <tbody>
            {children.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link className="rowlink name" to={`/jobs/${c.id}`} viewTransition translate="no">
                    {jobName(c)}
                  </Link>
                </td>
                <td>
                  <StateCell job={c} now={now} />
                </td>
                <td className="r">
                  {dayKey(c.submitted_at) !== dayKey(job.submitted_at) ? (
                    <Time value={c.submitted_at}>
                      <span className="text-ink-faint">{utc(c.submitted_at, "date")}</span> {utc(c.submitted_at)}
                    </Time>
                  ) : (
                    <Time value={c.submitted_at} mode="clock" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
